import React, {createContext, useCallback, useContext, useRef, useState, PropsWithChildren, MutableRefObject} from 'react';

let _nextId = 1;

/** A single pre-computed match for speaker spotlights. */
export interface SpotlightMatchItem {
    /** Actual line content — used to search the PDF text layer */
    text: string;
    /** Line index in the text viewer (for highlighting + scroll-to) */
    lineIndex: number;
}

export interface SpotlightItem {
    id: string;
    text: string;
    source: string;
    color?: string;
    visible: boolean;
    /** Optional page number hint, used to sort the spotlight bar by document position */
    pageHint?: number;
    /** True for the temporary "sync" spotlight created by split-view line clicks */
    isSync?: boolean;
    /** Spotlight kind: 'text' searches for literal text, 'speaker' uses pre-computed matchItems, 'page' jumps to pageHint, 'line' highlights one line + PDF-searches expanded context */
    kind?: 'text' | 'speaker' | 'page' | 'line';
    /** Pre-computed match items for speaker spotlights (line content + indices) */
    matchItems?: SpotlightMatchItem[];
}

export interface ActiveMatchInfo {
    currentMatch: number;
    totalMatches: number;
}

/**
 * A jump request — stored as state so effects can react to it.
 * Contains everything needed to find/create the spotlight and trigger the search.
 */
export interface JumpRequest {
    text: string;
    source: string;
    color?: string;
    pageHint?: number;
    seq: number; // monotonic, ensures every jump is unique
}

/** Common input shape for adding/jumping/toggling spotlights */
export interface SpotlightRequest {
    text: string;
    source: string;
    color?: string;
    pageHint?: number;
    kind?: 'text' | 'speaker' | 'page' | 'line';
    matchItems?: SpotlightMatchItem[];
}

/** Navigation functions that text-based viewers register for prev/next scrubbing */
export interface TextNavHandle {
    jumpToNextMatch: () => void;
    jumpToPreviousMatch: () => void;
}

/** A page-only jump request — no text search, just navigate to the page. */
export interface PageJumpRequest {
    page: number;   // 1-based page number
    seq: number;
}

interface DocumentSpotlightContextType {
    spotlights: SpotlightItem[];
    addSpotlight: (request: SpotlightRequest) => string;
    removeSpotlight: (id: string) => void;
    toggleSpotlightVisibility: (id: string) => void;
    clearAllSpotlights: () => void;
    activeSpotlightId: string | null;
    setActiveSpotlightId: (id: string | null) => void;
    activeMatchInfo: ActiveMatchInfo;
    updateMatchInfo: (currentMatch: number, totalMatches: number) => void;
    findByText: (text: string) => SpotlightItem | undefined;
    toggleOrAddSpotlight: (request: SpotlightRequest) => string;
    /**
     * Jump to a quote in the document. This:
     * 1. Ensures the spotlight exists and is visible
     * 2. Sets a jumpRequest that viewers react to
     * Viewers watch `jumpRequest` (not activeSpotlightId) to trigger search + scroll.
     */
    jumpToSpotlight: (request: SpotlightRequest) => void;
    /** The latest jump request — viewers watch this to trigger search + scroll */
    jumpRequest: JumpRequest | null;
    /** Jump directly to a page without text search (e.g., examination blocks) */
    jumpToPage: (page: number) => void;
    /** The latest page-only jump request — viewers watch this to navigate */
    pageJumpRequest: PageJumpRequest | null;
    /** Ref for text viewer navigation — text viewers write to this so the container can call prev/next */
    textNavRef: MutableRefObject<TextNavHandle | null>;
    /**
     * Set/replace the temporary sync spotlight (used in split view).
     * Removes any previous sync spotlight, adds a new one, and triggers a jump.
     */
    setSyncSpotlight: (request: SpotlightRequest) => void;
    /** Remove all sync spotlights. */
    clearSyncSpotlights: () => void;
    /** Queue a spotlight that survives the next clearAllSpotlights (for cross-document navigation). */
    queueJump: (request: SpotlightRequest) => void;
}

const DocumentSpotlightContext = createContext<DocumentSpotlightContextType | undefined>(undefined);

export const DocumentSpotlightProvider: React.FC<PropsWithChildren> = ({children}) => {
    const [spotlights, setSpotlights] = useState<SpotlightItem[]>([]);
    const [activeSpotlightId, setActiveSpotlightIdState] = useState<string | null>(null);
    const [activeMatchInfo, setActiveMatchInfo] = useState<ActiveMatchInfo>({currentMatch: 0, totalMatches: 0});
    const [jumpRequest, setJumpRequest] = useState<JumpRequest | null>(null);
    const [pageJumpRequest, setPageJumpRequest] = useState<PageJumpRequest | null>(null);
    const jumpSeqRef = useRef(0);
    const pageJumpSeqRef = useRef(0);
    const textNavRef = useRef<TextNavHandle | null>(null);
    const pendingJumpRef = useRef<SpotlightRequest | null>(null);
    // Mirror of spotlights for synchronous ID resolution inside jumpToSpotlight.
    // useCallback([], []) can't read spotlights directly, and the functional-updater
    // pattern would resolve the id too late for setActiveSpotlightIdState in the
    // same batch. The ref is updated on every render so reads are always current.
    const spotlightsRef = useRef<SpotlightItem[]>([]);
    spotlightsRef.current = spotlights;

    const addSpotlight = useCallback((request: SpotlightRequest) => {
        // Dedupe by text — if a spotlight with this text already exists, reuse it
        let returnedId = '';
        setSpotlights(prev => {
            const existing = prev.find(s => s.text === request.text);
            if (existing) {
                returnedId = existing.id;
                // Ensure visible + fill in pageHint if now known
                return prev.map(s => s.id === existing.id
                    ? {...s, visible: true, pageHint: s.pageHint ?? request.pageHint}
                    : s
                );
            }
            const id = `spotlight-${_nextId++}`;
            returnedId = id;
            return [...prev, {...request, id, visible: true}];
        });
        setActiveSpotlightIdState(returnedId);
        return returnedId;
    }, []);

    const removeSpotlight = useCallback((id: string) => {
        setSpotlights(prev => prev.filter(s => s.id !== id));
        setActiveSpotlightIdState(prev => prev === id ? null : prev);
    }, []);

    const toggleSpotlightVisibility = useCallback((id: string) => {
        setSpotlights(prev => prev.map(s => s.id === id ? {...s, visible: !s.visible} : s));
    }, []);

    const clearAllSpotlights = useCallback(() => {
        setSpotlights([]);
        setActiveSpotlightIdState(null);
        setActiveMatchInfo({currentMatch: 0, totalMatches: 0});
        setJumpRequest(null);
        // Apply pending cross-doc jump if queued (e.g. cross-document navigation)
        const pending = pendingJumpRef.current;
        if (pending) {
            pendingJumpRef.current = null;
            // Inline the jumpToSpotlight logic — add spotlight then fire jump request
            const id = `spotlight-${_nextId++}`;
            setSpotlights([{...pending, id, visible: true}]);
            jumpSeqRef.current += 1;
            setJumpRequest({...pending, seq: jumpSeqRef.current});
        }
    }, []);

    const setActiveSpotlightId = useCallback((id: string | null) => {
        setActiveSpotlightIdState(id);
    }, []);

    const updateMatchInfo = useCallback((currentMatch: number, totalMatches: number) => {
        setActiveMatchInfo({currentMatch, totalMatches});
    }, []);

    const findByText = useCallback((text: string) => {
        return spotlights.find(s => s.text === text);
    }, [spotlights]);

    const toggleOrAddSpotlight = useCallback((request: SpotlightRequest) => {
        const existing = spotlights.find(s => s.text === request.text);
        if (existing) {
            setSpotlights(prev => prev.filter(s => s.id !== existing.id));
            setActiveSpotlightIdState(prev => prev === existing.id ? null : prev);
            return existing.id;
        }
        const id = `spotlight-${_nextId++}`;
        setSpotlights(prev => [...prev, {...request, id, visible: true}]);
        return id;
    }, [spotlights]);

    const setSyncSpotlight = useCallback((request: SpotlightRequest) => {
        // Remove any existing sync spotlight, then add the new one.
        // Set activeSpotlightId synchronously (like jumpToSpotlight does) so
        // useLineSpotlight scrolls to the clicked line, not a stale spotlight.
        const id = `spotlight-${_nextId++}`;
        setSpotlights(prev => {
            const withoutSync = prev.filter(s => !s.isSync);
            return [...withoutSync, {...request, id, visible: true, isSync: true}];
        });
        setActiveSpotlightIdState(id);
        jumpSeqRef.current += 1;
        setJumpRequest({...request, seq: jumpSeqRef.current});
    }, []);

    const clearSyncSpotlights = useCallback(() => {
        setSpotlights(prev => prev.filter(s => !s.isSync));
    }, []);

    const queueJump = useCallback((request: SpotlightRequest) => {
        pendingJumpRef.current = request;
    }, []);

    const jumpToPage = useCallback((page: number) => {
        pageJumpSeqRef.current += 1;
        setPageJumpRequest({page, seq: pageJumpSeqRef.current});
    }, []);

    const jumpToSpotlight = useCallback((request: SpotlightRequest) => {
        // Resolve the target spotlight ID synchronously via spotlightsRef so we
        // can set activeSpotlightId in the same batch as the jumpRequest. If we
        // skipped this, useLineSpotlight's effect (a child) would run before
        // the parent's effect that updates activeSpotlightId, and navRanges
        // would fall back to all visible spotlights — scrolling to the wrong
        // location on first click of a person/quote in MetadataPalette.
        const existing = spotlightsRef.current.find(s => s.text === request.text);
        let resolvedId: string;
        if (existing) {
            resolvedId = existing.id;
            if (!existing.visible || (existing.pageHint == null && request.pageHint != null)) {
                setSpotlights(prev => prev.map(s => s.id === existing.id
                    ? {...s, visible: true, pageHint: s.pageHint ?? request.pageHint}
                    : s
                ));
            }
        } else {
            resolvedId = `spotlight-${_nextId++}`;
            const newSpotlight: SpotlightItem = {...request, id: resolvedId, visible: true};
            setSpotlights(prev => [...prev, newSpotlight]);
        }

        setActiveSpotlightIdState(resolvedId);

        jumpSeqRef.current += 1;
        setJumpRequest({...request, seq: jumpSeqRef.current});
    }, []);

    return (
        <DocumentSpotlightContext.Provider value={{
            spotlights, addSpotlight, removeSpotlight, toggleSpotlightVisibility,
            clearAllSpotlights, activeSpotlightId, setActiveSpotlightId,
            activeMatchInfo, updateMatchInfo, findByText,
            toggleOrAddSpotlight, jumpToSpotlight, jumpRequest,
            jumpToPage, pageJumpRequest, textNavRef,
            setSyncSpotlight, clearSyncSpotlights, queueJump,
        }}>
            {children}
        </DocumentSpotlightContext.Provider>
    );
};

export const useDocumentSpotlight = () => useContext(DocumentSpotlightContext);
